import { cartLineOf, useStore, type CartItem } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { showToast } from '../components/Toast'

/**
 * サーバが注文を弾いたとき、**カートを直して、客が確認してからもう一度送れる状態に戻す。**
 *
 * 注文を送る口は 4 つ（卓のカート・先払いテイクアウト・レジ受付・出前）あり、
 * **1 つでも欠けるとその経路だけ詰まる**ので 1 か所に置く。行を残したままだと、客が何度
 * 押しても同じ理由で弾かれ、どの品が原因かも分からない（`items_sold_out` と同じ考え方）。
 *
 * 扱うのは 3 つ。どれも「カートに入れた後で店のメニューが変わった」が正直な原因。
 *
 * - `price_changed` (#2042) … **値段が変わった。** 請求額はサーバがメニューから決めるので、
 *   画面の値段が古いと、見せた額と違う額を請求することになる。サーバはそれを弾いて今の値段を
 *   返してくる。カートの金額を直し、**客が新しい金額を見てから**もう一度押してもらう
 * - `unknown_menu_item` (#2042) … 商品がメニューから消えた。その行を外す
 * - `invalid_variation` (#2031) … 選んだ選択肢が売り切れ・変更になった。その行を外す
 *
 * サーバは弾いた行を `index`（送った items の中の位置）で返す。商品 ID だけで外すと、
 * 「もも・塩」と「もも・たれ」のうち通るはずの塩まで消える。
 *
 * @returns 処理したら true。呼び出し側は true のとき、いつもの失敗トーストを出さない。
 */
export function useCartRejectionRecovery(
  items: CartItem[],
  onMenuStale?: () => void | Promise<void>,
) {
  const { t } = useI18n()
  const removeFromCart = useStore((s) => s.removeFromCart)
  const applyCartPrices = useStore((s) => s.applyCartPrices)

  return async (error: unknown): Promise<boolean> => {
    const err = error as {
      code?: string
      detail?: {
        index?: unknown
        menuItemId?: unknown
        items?: { index?: unknown; currentPrice?: unknown }[]
      }
    }
    const code = err?.code
    if (code !== 'price_changed' && code !== 'unknown_menu_item' && code !== 'invalid_variation') {
      return false
    }

    if (code === 'price_changed') {
      const changes = (err.detail?.items ?? [])
        .filter((c) => typeof c.index === 'number' && typeof c.currentPrice === 'number')
        .map((c) => ({ line: items[c.index as number], basePrice: c.currentPrice as number }))
        .filter((c) => !!c.line)
        .map((c) => ({ lineId: cartLineOf(c.line), basePrice: c.basePrice }))
      applyCartPrices(changes)
    } else {
      const index = err.detail?.index
      const target = typeof index === 'number' ? items[index] : undefined
      // 位置が取れない (古いサーバ) ときだけ、その商品の行をまとめて外す
      const lines = target ? [target] : items.filter((i) => i.menuItemId === err.detail?.menuItemId)
      lines.forEach((i) => removeFromCart(cartLineOf(i)))
    }

    // メニューを取り直す。取り直さないと、古い値段・切れた選択肢・消えた商品がまだ画面に残る
    await onMenuStale?.()
    showToast({
      type: 'error',
      message: t(
        code === 'price_changed' ? 'cart.priceChanged'
          : code === 'unknown_menu_item' ? 'cart.itemUnavailable'
            : 'cart.variationChanged',
      ),
    })
    return true
  }
}
